// Scrap or loss at a CM, part by part with a reason (optionally against one of its make orders); the lines are the
// usual table (DataTable), typed into their cells.
import { useMemo, useState } from "react";
import { type CmSummary, SCRAP_REASON_LABELS, SCRAP_REASONS, type ScrapReason } from "../../../../shared/consignment.ts";
import type { Item } from "../../../../shared/db.gen.ts";
import { qty } from "../../lib/format.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { Button, cx, Input, Select, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { toast } from "../../ui/toast.tsx";
import { ItemPicker, Modal, parseQty } from "./parts.tsx";

export const reasonOptions = SCRAP_REASONS.map((r) => ({ value: r, label: SCRAP_REASON_LABELS[r] }));

type Line = { item: Item; qty: string; reason: ScrapReason; notes: string };
type ScrapRow = Line & { id: string; added: number };

export function ScrapDialog({ cm, onClose }: { cm: CmSummary; onClose: () => void }) {
  const [lines, setLines] = useState<Line[]>([]);
  const [build, setBuild] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [id] = useState(() => crypto.randomUUID());
  const onHand = useMemo(() => new Map(cm.stock.map((l) => [l.itemId, l.onHand])), [cm]);

  const submit = async () => {
    if (!lines.length) return toast.error("Add what was scrapped");
    const bad = lines.find((l) => !((parseQty(l.qty) ?? 0) > 0));
    if (bad) return toast.error(`Enter how many ${bad.item.sku || bad.item.name}`);
    setBusy(true);
    const { ok, result } = await mutateWithResult("cm.report_scrap", {
      id,
      location_id: cm.location.id,
      make_order_id: build || undefined,
      notes: notes.trim(),
      lines: lines.map((l) => ({ item_id: l.item.id, qty: parseQty(l.qty) as number, reason: l.reason, notes: l.notes.trim() })),
    });
    setBusy(false);
    if (!ok) return;
    toast.ok("Scrap recorded");
    for (const w of (result as { warnings?: string[] }).warnings ?? []) toast.info(w);
    onClose();
  };

  // The lines: the usual table; how many, the reason and a note are typed or picked in their cells.
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- in the toolbar
  const rows = useMemo<ScrapRow[]>(() => lines.map((l, i) => ({ ...l, id: l.item.id, added: i })), [lines]);
  const set = (itemId: string, patch: Partial<Line>) => setLines((ls) => ls.map((x) => (x.item.id === itemId ? { ...x, ...patch } : x)));
  const columns: Column<ScrapRow>[] = [
    { key: "photo", header: "", width: "40px", value: () => null, cell: (l) => <Thumb src={l.item.photo_url} size={24} /> },
    { key: "sku", header: "SKU", width: "120px", value: (l) => l.item.sku, cell: (l) => <span className="truncate font-mono text-[12px]">{l.item.sku}</span> },
    { key: "name", header: "Part", width: "minmax(150px,1fr)", value: (l) => l.item.name, cell: (l) => <span className="truncate">{l.item.name}</span> },
    {
      key: "there",
      header: "There",
      width: "80px",
      align: "right",
      value: (l) => onHand.get(l.item.id) ?? 0,
      cell: (l) => <span className="font-mono text-[12px] text-muted">{qty(onHand.get(l.item.id) ?? 0) || "0"}</span>,
    },
    {
      key: "qty",
      header: "Scrapped",
      width: "100px",
      align: "right",
      value: (l) => parseQty(l.qty) ?? l.qty,
      text: (l) => l.qty,
      cell: (l) => <span className={cx("font-mono text-[12px]", !l.qty && "text-faint")}>{l.qty || "—"}</span>,
      edit: { get: (l) => l.qty, save: (l, text) => set(l.item.id, { qty: text.trim() }) },
    },
    {
      key: "reason",
      header: "Reason",
      width: "150px",
      value: (l) => SCRAP_REASON_LABELS[l.reason],
      edit: { options: reasonOptions, get: (l) => l.reason, save: (l, value) => set(l.item.id, { reason: value as ScrapReason }) },
    },
    {
      key: "notes",
      header: "Note",
      width: "minmax(140px,1fr)",
      value: (l) => l.notes,
      edit: { get: (l) => l.notes, save: (l, text) => set(l.item.id, { notes: text }) },
    },
    // The order they were added in (what it's sorted by until someone sorts it another way).
    { key: "added", header: "#", optional: true, width: "48px", align: "right", value: (l) => l.added + 1 },
    {
      key: "remove",
      header: "",
      width: "40px",
      value: () => null,
      cell: (l) => (
        <button
          type="button"
          className="text-faint hover:text-danger"
          title="Remove"
          onClick={(e) => {
            e.stopPropagation();
            setLines((ls) => ls.filter((x) => x.item.id !== l.item.id));
          }}
        >
          ×
        </button>
      ),
    },
  ];

  return (
    <Modal
      title={`Record scrap or loss at ${cm.location.name}`}
      onClose={onClose}
      width={860}
      footer={
        <>
          <div className="w-56 shrink-0">
            <Select
              aria-label="Make order"
              value={build}
              onChange={(e) => setBuild(e.target.value)}
              options={[
                { value: "", label: "Not tied to a build" },
                ...cm.orders.filter((o) => o.open || o.output > 0).map((o) => ({ value: o.mo.id, label: `${o.po.number} ${o.item?.sku ?? ""}` })),
              ]}
              className="h-8"
            />
          </div>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" className="w-64" aria-label="Notes" />
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" disabled={busy || !lines.length} onClick={submit}>
            Write off
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-2 border-b border-line px-4 py-2.5">
        <ItemPicker
          className="w-80"
          allow={(i) => i.kind !== "service" && !lines.some((l) => l.item.id === i.id)}
          hint={(i) => `${qty(onHand.get(i.id) ?? 0) || "0"} there`}
          onPick={(item) => setLines((ls) => [...ls, { item, qty: "", reason: "defective", notes: "" }])}
          placeholder="Add a part: SKU or name…"
        />
        <div ref={setSlot} className="ml-auto flex shrink-0 items-center gap-1" />
      </div>
      <div className="px-4 pt-2.5 pb-3">
        <DataTable
          fit={14}
          rows={rows}
          columns={columns}
          editing
          defaultSort={{ key: "added" }}
          rowNoun={["part", "parts"]}
          exportName="Scrap lines"
          viewsSlot={slot}
          pageFilters={false}
          empty="Search for the parts the CM scrapped or lost."
        />
      </div>
    </Modal>
  );
}
